import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Outlet, useParams, useRouterState, useSearch } from '@tanstack/react-router';
import { useRef, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import { useReimbursementEligibility } from '../api/reimbursementApi';
import { ReimbursementBackButton } from '../components/ReimbursementBackButton';
import { ReimbursementBlockerNotice } from '../components/ReimbursementBlockerNotice';
import { ReimbursementStepper } from '../components/ReimbursementStepper';
import { toReimbursementOrigin } from '../routes';
import { useReimbursementDraft } from '../state/reimbursementDraftContext';
import { ReimbursementDraftProvider } from '../state/ReimbursementDraftProvider';
import { isReimbursementStepId, REIMBURSEMENT_STEPS } from '../steps';
import type { ReimbursementStepId } from '../types';

// Retain the last real step so the stepper doesn't flash back to step one while leaving the flow.
function useCurrentStep(): ReimbursementStepId {
    const pathname = useRouterState({ select: (state) => state.location.pathname });
    const segment = pathname.split('/').at(-1) ?? '';
    const derived = isReimbursementStepId(segment) ? segment : undefined;
    const lastStep = useRef<ReimbursementStepId>(derived ?? REIMBURSEMENT_STEPS[0].id);
    if (derived) {
        lastStep.current = derived;
    }
    return lastStep.current;
}

function ReimbursementFlow({ currentStep }: { currentStep: ReimbursementStepId }): ReactElement {
    const { t } = useTranslation();
    const { draft, offerRequestLeadUuid, origin } = useReimbursementDraft();
    const eligibility = useReimbursementEligibility(offerRequestLeadUuid);

    return (
        <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', px: { xs: 2, sm: 3 } }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 2 }}
                sx={{ alignItems: { sm: 'center' }, py: 2 }}>
                <ReimbursementBackButton
                    currentStep={currentStep}
                    offerRequestLeadUuid={offerRequestLeadUuid}
                    origin={origin}
                    exitOnly={draft.submitted}
                />
                <Typography variant="h6" component="h1" sx={{ flexShrink: 0 }}>
                    {t('REIMBURSEMENT.TITLE')}
                </Typography>
                {eligibility.data?.canRequest === true && !draft.submitted && (
                    <ReimbursementStepper currentStep={currentStep} />
                )}
            </Stack>

            {match(eligibility)
                .with({ status: 'pending' }, () => (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress aria-label={t('ACTIONS.LOADING')} />
                    </Stack>
                ))
                .with({ status: 'error' }, () => <Alert severity="error">{t('REIMBURSEMENT.LOAD_ERROR')}</Alert>)
                .with({ status: 'success' }, ({ data }) =>
                    data.blocker === null || draft.submitted ? (
                        <Outlet />
                    ) : (
                        <ReimbursementBlockerNotice blocker={data.blocker} />
                    ),
                )
                .exhaustive()}
        </Box>
    );
}

export function ReimbursementLayout(): ReactElement {
    const currentStep = useCurrentStep();
    const { offerRequestLeadUuid } = useParams({ strict: false });
    const { from } = useSearch({ strict: false });

    return (
        <ReimbursementDraftProvider
            offerRequestLeadUuid={offerRequestLeadUuid ?? ''}
            origin={toReimbursementOrigin(from)}>
            <ReimbursementFlow currentStep={currentStep} />
        </ReimbursementDraftProvider>
    );
}
